(() => {
  if (window.__merinGa4CustomEventsInitialized) return;
  window.__merinGa4CustomEventsInitialized = true;

  const GA4_MEASUREMENT_ID = 'G-0XSRMGH8TT';

  const SELECTORS = {
    list: '[data-ga4-product-list="true"]',
    trackedList:
      '[data-ga4-product-list="true"][data-ga4-track-view-item-list="true"]',
    item: '[data-ga4-product-item="true"]'
  };

  const observedLists = new WeakSet();

  const waitForGtag = (callback, attempt = 0) => {
    if (typeof window.gtag === 'function') {
      callback();
      return;
    }

    if (attempt >= 40) {
      console.warn('[GA4] gtag is not available.');
      return;
    }

    setTimeout(() => {
      waitForGtag(callback, attempt + 1);
    }, 250);
  };

  const getOwnedProductItems = (list) => {
    return Array.from(list.querySelectorAll(SELECTORS.item)).filter(
      (item) => item.closest(SELECTORS.list) === list
    );
  };

  const getListContext = (item, list = null) => {
    return {
      itemListId:
        list?.dataset.ga4ItemListId ||
        item.dataset.ga4ItemListId ||
        '',
      itemListName:
        list?.dataset.ga4ItemListName ||
        item.dataset.ga4ItemListName ||
        ''
    };
  };

  const resolveIndex = (item, list = null) => {
    const explicitIndex = Number(item.dataset.ga4Index);

    if (
      item.dataset.ga4Index !== undefined &&
      Number.isFinite(explicitIndex)
    ) {
      return explicitIndex;
    }

    if (list) {
      const items = getOwnedProductItems(list);
      const index = items.indexOf(item);
      return index >= 0 ? index : 0;
    }

    const visualList = item.closest('ul, ol, [role="list"]');

    if (visualList) {
      const items = Array.from(
        visualList.querySelectorAll(SELECTORS.item)
      ).filter((candidate) => {
        return candidate.closest('ul, ol, [role="list"]') === visualList;
      });

      const index = items.indexOf(item);
      return index >= 0 ? index : 0;
    }

    return 0;
  };

  const buildItem = (element, index, list = null) => {
    const itemId = element.dataset.ga4ItemId;
    const itemName = element.dataset.ga4ItemName;

    if (!itemId || !itemName) {
      return null;
    }

    const { itemListId, itemListName } = getListContext(element, list);

    const item = {
      item_id: itemId,
      item_name: itemName,
      index
    };

    if (itemListId) {
      item.item_list_id = itemListId;
    }

    if (itemListName) {
      item.item_list_name = itemListName;
    }

    if (element.dataset.ga4ItemBrand) {
      item.item_brand = element.dataset.ga4ItemBrand;
    }

    if (element.dataset.ga4ItemVariant) {
      item.item_variant = element.dataset.ga4ItemVariant;
    }

    if (element.dataset.ga4Sku) {
      item.sku = element.dataset.ga4Sku;
    }

    if (element.dataset.ga4VariantId) {
      item.variant_id = element.dataset.ga4VariantId;
    }

    const price = Number(element.dataset.ga4Price);

    if (Number.isFinite(price)) {
      item.price = price;
    }

    return item;
  };

  const sendViewItemList = (list) => {
    if (list.dataset.ga4ViewItemListSent === 'true') return;

    const itemListId = list.dataset.ga4ItemListId || '';
    const itemListName = list.dataset.ga4ItemListName || '';

    if (!itemListId || !itemListName) {
      console.warn(
        '[GA4] view_item_list skipped because list id/name is missing.',
        list
      );
      return;
    }

    const productItems = getOwnedProductItems(list);

    if (!productItems.length) return;

    const items = productItems
      .map((productItem) => {
        return buildItem(
          productItem,
          resolveIndex(productItem, list),
          list
        );
      })
      .filter(Boolean);

    if (!items.length) return;

    waitForGtag(() => {
      if (list.dataset.ga4ViewItemListSent === 'true') return;

      window.gtag('event', 'view_item_list', {
        send_to: GA4_MEASUREMENT_ID,
        currency: productItems[0].dataset.ga4Currency || 'JPY',
        item_list_id: itemListId,
        item_list_name: itemListName,
        items
      });

      list.dataset.ga4ViewItemListSent = 'true';
      intersectionObserver.unobserve(list);
    });
  };

  const intersectionObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;

        const list = entry.target;
        sendViewItemList(list);

        if (list.dataset.ga4ViewItemListSent === 'true') {
          intersectionObserver.unobserve(list);
        }
      });
    },
    {
      threshold: 0.1
    }
  );

  const initTrackedList = (list) => {
    if (observedLists.has(list)) return;
    observedLists.add(list);

    if (list.dataset.ga4ViewTrigger === 'details-open') {
      const details = list.closest('details');

      if (!details) {
        console.warn(
          '[GA4] details-open list has no parent <details>.',
          list
        );
        return;
      }

      const handleToggle = () => {
        if (list.dataset.ga4ViewItemListSent === 'true') return;

        if (details.open) {
          intersectionObserver.observe(list);
        } else {
          intersectionObserver.unobserve(list);
        }
      };

      details.addEventListener('toggle', handleToggle);
      handleToggle();

      return;
    }

    intersectionObserver.observe(list);
  };

  const initTrackedLists = (root = document) => {
    if (root.matches?.(SELECTORS.trackedList)) {
      initTrackedList(root);
    }

    root.querySelectorAll?.(SELECTORS.trackedList).forEach((list) => {
      initTrackedList(list);
    });
  };

  const sendSelectItemAndNavigate = (event, link, params) => {
    if (typeof window.gtag !== 'function') {
      console.warn('[GA4] gtag is not available.');
      return;
    }

    const isNormalNavigation =
      event.button === 0 &&
      !event.ctrlKey &&
      !event.metaKey &&
      !event.shiftKey &&
      !event.altKey &&
      !link.hasAttribute('download') &&
      (!link.target || link.target === '_self');

    if (!isNormalNavigation) {
      window.gtag('event', 'select_item', params);
      return;
    }

    event.preventDefault();

    let navigated = false;

    const navigate = () => {
      if (navigated) return;
      navigated = true;
      window.location.href = link.href;
    };

    window.gtag('event', 'select_item', {
      ...params,
      event_callback: navigate,
      event_timeout: 800
    });

    setTimeout(navigate, 1000);
  };

  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[href]');

    if (!link) return;

    let linkUrl;

    try {
      linkUrl = new URL(link.href, window.location.origin);
    } catch (error) {
      return;
    }

    if (!linkUrl.pathname.includes('/products/')) return;

    const productItem = link.matches(SELECTORS.item)
      ? link
      : link.closest(SELECTORS.item);

    if (!productItem) return;

    const list = productItem.closest(SELECTORS.list);
    const { itemListId, itemListName } = getListContext(
      productItem,
      list
    );

    if (!itemListId || !itemListName) {
      console.warn(
        '[GA4] select_item skipped because list id/name is missing.',
        productItem
      );
      return;
    }

    const item = buildItem(
      productItem,
      resolveIndex(productItem, list),
      list
    );

    if (!item) return;

    const params = {
      send_to: GA4_MEASUREMENT_ID,
      currency: productItem.dataset.ga4Currency || 'JPY',
      item_list_id: itemListId,
      item_list_name: itemListName,
      items: [item]
    };

    waitForGtag(() => {
      sendSelectItemAndNavigate(event, link, params);
    });
  });

  const init = () => {
    initTrackedLists(document);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initTrackedLists(event.target);
  });
})();
